import axios from 'axios';
import { Project, ProjectResponse } from '../types/project';
import { AboutUs, AboutUsResponse, AboutUsFormData } from '../types/about_us';
import { Banner, BannerResponse, BannerFormData } from '../types/banner';
import { Award, AwardResponse, AwardFormData } from '../types/award';
import { Review, ReviewsResponse } from '../types/review';
import type { LoginCredentials, SignupCredentials, LoginResponse } from '../types/auth';
import type {
  Property,
  PropertyAmenity,
  PropertyData,
  PropertyFormData,
  PropertyLocation,
  PropertyMedia,
  PropertyResponse,
  PropertySearchParams,
  ContactHostFormData,
  ContactHost,
} from '../types/propertyApi';
import type { DeveloperData, DeveloperResponse } from '../types/developer';
import type {
  TestimonialData,
  TestimonialFormData,
  TestimonialResponse,
  TestimonialSearchParams,
} from '../types/testimonial';
import type {
  BlogData,
  BlogResponse,
  BlogSearchParams,
  CommentData,
  CommentResponse,
} from '../types/blog';
import type { InquiryData, InquiryResponse } from '../types/inquiry';
import type { UserData, UserResponse } from '../types/user';
import type { FileData, FileResponse } from '../types/file';
import type { HostEnquiryData, HostEnquiryResponse } from '../types/hostEnquiry';
import type { PropertyTypeFormData, PropertyTypeResponse } from '../types/propertyType';
import type { ProjectTypeFormData, ProjectTypeResponse } from '../types/projectType';
import type { AreaFormData, AreaSearchParams, AreaResponse } from '../types/area';
import type { AgentData, AgentResponse } from '../types/agent';
import type {
  NewsletterSubscribeRequest,
  NewsletterSubscriber,
  NewsletterResponse,
} from '../types/newsletter';
import type { ContactUsData, ContactUsResponse } from '../types/contactUs';
import type {
  MortgageApplicantData,
  MortgageCreateRequest,
  MortgageCreateResponse,
  MortgageLoanDetails,
} from '../types/mortgage';
import type {
  PropertyServiceFormData,
  PropertyServiceResponse,
} from '../types/propertyService';
import type { FAQData, FAQFormData, FAQResponse } from '../types/faq';

// Re-export commonly used types for consumers of this module
export type { AreaFormData } from '../types/area';
export type { Property } from '../types/propertyApi';

// Helper function to safely access localStorage
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('authToken');
};

const removeAuthToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
};

const setAuthToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('authToken', token);
  }
};

const getBaseUrl = () => {
  const url = (process.env.NEXT_PUBLIC_API_BASE_URL || 'http://13.210.77.238:3002').trim();
  return url.replace(/\/+$/, '');
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getBaseUrl(),
  // baseURL: 'http://localhost:3002',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      removeAuthToken();
      if (typeof window !== 'undefined') {
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Create axios instance for public API calls (no auth redirect)
export const publicApi = axios.create({
  baseURL: getBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token in case it's available
publicApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Authentication functions
export const authAPI = {
  // Sign in with email/password
  signIn: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/v1/auth/login_password', credentials);

      if (response?.data?.data?.token) {
        const token = response?.data?.data?.token;
        setAuthToken(token);

        return {
          success: true,
          token: token,
          user: response?.data?.data?.user || response?.data?.user,
          message: response?.data?.message || 'Login successful'
        };
      }

      return {
        success: false,
        message: response?.data?.error || 'No token received from server'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  },

  // Sign up with user details
  signUp: async (credentials: SignupCredentials): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/v1/auth/customer/signup', credentials);

      if (response?.data?.status || response?.data?.meta?.status) {
        // Registration successful - don't auto-login, just return success
        return {
          success: true,
          message: response?.data?.message || response?.data?.meta?.message || 'Registration successful'
        };
      }

      return {
        success: false,
        message: response?.data?.message || response?.data?.meta?.message || 'Registration failed'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Registration failed'
      };
    }
  },

  // Sign out
  signOut: () => {
    removeAuthToken();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!getAuthToken();
  },

  setUserDetails: (user: any): void => {
    // Store user information if available
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }
  },

  // isAuthenticated: (): boolean => {
  //   if (typeof window !== "undefined") {
  //     return !!localStorage.getItem("authToken");
  //   }
  //   return false; // Default for SSR or non-browser environment
  // },

  // Get current auth token
  getToken: (): string | null => {
    return getAuthToken();
  },

  // Decode JWT token to get user information
  decodeToken: (): any | null => {
    try {
      const token = getAuthToken();
      if (!token) return null;

      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decode the payload (second part)
      const payload = parts[1];

      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);

      // Decode base64
      const decodedPayload = atob(paddedPayload);

      // Parse JSON
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  // Get current user information from token
  getCurrentUser: (): any | null => {
    const decodedToken = authAPI.decodeToken();
    return decodedToken;
  },

  // Host sign in with email/password
  hostSignIn: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/v1/auth/host/signin_password', credentials);

      if (response?.data?.data?.token) {
        const token = response?.data?.data?.token;
        setAuthToken(token);

        return {
          success: true,
          token: token,
          user: response?.data?.data?.user || response?.data?.user,
          message: response?.data?.message || 'Host login successful'
        };
      }

      return {
        success: false,
        message: response?.data?.error || 'No token received from server'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Host login failed'
      };
    }
  },

  // Host sign up
  hostSignUp: async (credentials: SignupCredentials): Promise<LoginResponse> => {
    try {
      const response = await api.post('/api/v1/auth/host/signup', credentials);

      if (response?.data?.status || response?.data?.meta?.status) {
        // Registration successful - don't auto-login, just return success
        return {
          success: true,
          message: response?.data?.message || response?.data?.meta?.message || 'Host registration successful'
        };
      }

      return {
        success: false,
        message: response?.data?.message || response?.data?.meta?.message || 'Host registration failed'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Host registration failed'
      };
    }
  }
};

// Generic API functions
export const apiClient = {
  get: api.get,
  post: api.post,
  put: api.put,
  delete: api.delete,
  patch: api.patch,
};

// Property API functions
export const propertyAPI = {
  // Create new property
  create: async (propertyData: PropertyData): Promise<PropertyResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/property/create', propertyData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data,
        message: 'Property created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create property'
      };
    }
  },

  // Get all properties
  getAll: async (page?: number): Promise<PropertyResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const params = new URLSearchParams();
      if (page !== undefined && page !== null) {
        params.append('page', String(page));
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/mr-alfred/properties?${queryString}` : '/api/v1/mr-alfred/properties';

      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch properties'
      };
    }
  },

  // Get property by ID
  getById: async (id: string): Promise<PropertyResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/mr-alfred/properties/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property'
      };
    }
  },

  // Update property
  update: async (id: string, propertyData: Partial<PropertyData>): Promise<PropertyResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/property/${id}/update`, propertyData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data,
        message: 'Property updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update property'
      };
    }
  },

  // Delete property
  delete: async (id: string): Promise<PropertyResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/property/${id}/delete`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        message: 'Property deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete property'
      };
    }
  },

  // Get all properties for web with search parameters
  getAllWeb: async (searchParams?: PropertySearchParams): Promise<PropertyResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // Handle array values (like amenities)
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      // const url = queryString ? `/api/v1/property/web/list?${queryString}` : '/api/v1/property/web/list';
      const url = `/api/v1/mr-alfred/properties${queryString ? '?status=Published&' + queryString : '?status=Published'}`;

      const response = await publicApi.get(url);
      return {
        success: true,
        data: response.data.data,
        message: 'Properties fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch properties'
      };
    }
  },

  // Get property by slug (for frontend)
  getBySlug: async (slug: string): Promise<PropertyResponse> => {
    try {
      const response = await publicApi.get(`/api/v1/property/get-by-slug/${slug}`);
      return {
        success: true,
        data: response.data,
        message: 'Property fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property'
      };
    }
  },

  // Get property by ID (for frontend)
  getByIdWeb: async (id: string): Promise<PropertyResponse> => {
    try {
      // Try mr-alfred endpoint first as it seems to be the main one now
      const response = await publicApi.get(`/api/v1/mr-alfred/properties/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Property fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property'
      };
    }
  },
};

// Project API functions
export const projectAPI = {
  // Create new project
  create: async (projectData: Project): Promise<ProjectResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/project/create', projectData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data,
        message: 'Project created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create project'
      };
    }
  },

  // Get all projects
  getAll: async (): Promise<ProjectResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/project', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data,
        message: 'Projects fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch projects'
      };
    }
  },

  // Get project by ID
  getById: async (id: string): Promise<ProjectResponse> => {
    try {

      const response = await api.get(`/api/v1/project/get/${id}`, {

      });
      return {
        success: true,
        data: response.data,
        message: 'Project fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch project'
      };
    }
  },

  // Update project
  update: async (id: string, projectData: Partial<Project>): Promise<ProjectResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/project/${id}/update`, projectData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data,
        message: 'Project updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update project'
      };
    }
  },

  // Delete project
  delete: async (id: string): Promise<ProjectResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/project/${id}/delete`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        message: 'Project deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete project'
      };
    }
  },

  // Get all projects for web with search parameters
  getAllWeb: async (searchParams?: any): Promise<ProjectResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // Handle array values (like amenities)
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/project/web/list?${queryString}` : '/api/v1/project/web/list';

      const response = await publicApi.get(url);
      return {
        success: true,
        data: response.data.data,
        message: 'Projects fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch projects'
      };
    }
  },
};

// Upload file API functions
export const uploadFileAPI = {
  // Upload file
  uploadFile: async (fileData: FileData): Promise<FileResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/auth/upload', fileData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return {
        success: true,
        data: response.data.data,
        message: 'File uploaded successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to upload file'
      };
    }
  }
};

// Developer API functions
export const developerAPI = {
  // Create new developer
  create: async (developerData: DeveloperData): Promise<DeveloperResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/developer/create', developerData);
      return {
        success: true,
        data: response.data,
        message: 'Developer created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create developer'
      };
    }
  },

  // Get all developers
  getAll: async (): Promise<DeveloperResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/developer');
      return {
        success: true,
        data: response.data,
        message: 'Developers fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch developers'
      };
    }
  },

  // Get developer by ID
  getById: async (id: string): Promise<DeveloperResponse> => {
    try {
      // Check if user is authenticated
      //const token = localStorage.getItem('authToken');
      //if (!token) {
      //  return {
      //    success: false,
      //    message: 'Authentication required. Please login again.'
      //  };
      //}

      const response = await api.get(`/api/v1/developer/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Developer fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch developer'
      };
    }
  },

  // Update developer
  update: async (id: string, developerData: Partial<DeveloperData>): Promise<DeveloperResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/developer/${id}/update`, developerData);
      return {
        success: true,
        data: response.data,
        message: 'Developer updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update developer'
      };
    }
  },

  // Delete developer
  delete: async (id: string): Promise<DeveloperResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/developer/${id}/delete`);
      return {
        success: true,
        message: 'Developer deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete developer'
      };
    }
  },

  // Get all developers web
  getAllWeb: async (searchParams?: any): Promise<DeveloperResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/developer/web/list?${queryString}` : '/api/v1/developer/web/list';

      const response = await publicApi.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Developers fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch developers'
      };
    }
  },
};

// Testimonial API functions
export const testimonialAPI = {
  // Create new testimonial
  create: async (testimonialData: TestimonialData): Promise<TestimonialResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/testimonials/create', testimonialData);
      return {
        success: true,
        data: response.data,
        message: 'Testimonial created successfully'
      };
    } catch (error: any) {
      console.error('Error creating testimonial:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create testimonial'
      };
    }
  },

  // Get all testimonials with optional search parameters
  getAll: async (searchParams?: TestimonialSearchParams): Promise<TestimonialResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/testimonials?${queryString}` : '/api/v1/testimonials';

      const response = await api.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Testimonials fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching testimonials:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch testimonials'
      };
    }
  },

  // Get testimonial by ID
  getById: async (id: string): Promise<TestimonialResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/testimonials/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Testimonial fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching testimonial:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch testimonial'
      };
    }
  },

  // Update testimonial
  update: async (id: string, testimonialData: TestimonialData): Promise<TestimonialResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/testimonials/${id}/update`, testimonialData);
      return {
        success: true,
        data: response.data,
        message: 'Testimonial updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating testimonial:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update testimonial'
      };
    }
  },

  // Delete testimonial
  delete: async (id: string): Promise<TestimonialResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/testimonials/${id}/delete`);
      return {
        success: true,
        message: 'Testimonial deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting testimonial:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete testimonial'
      };
    }
  },

  // Get all testimonials web
  getAllWeb: async (): Promise<TestimonialResponse> => {
    try {
      const response = await publicApi.get('/api/v1/testimonials/web/list');
      return {
        success: true,
        data: response.data,
        message: 'Testimonials fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching testimonials:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch testimonials'
      };
    }
  }
};

// Inquiry API functions
export const inquiryAPI = {
  // Create new inquiry
  create: async (inquiryData: InquiryData): Promise<InquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/enquiry/create', inquiryData);
      return {
        success: true,
        data: response.data,
        message: 'Inquiry created successfully'
      };
    } catch (error: any) {
      console.error('Error creating inquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create inquiry'
      };
    }
  },

  // Get all inquiries
  getAll: async (): Promise<InquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/enquiry');
      return {
        success: true,
        data: response.data,
        message: 'enquiry fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch inquiries'
      };
    }
  },

  // Get inquiry by ID
  getById: async (id: string): Promise<InquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/enquiry/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'enquiry fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch inquiry'
      };
    }
  },

  // Update inquiry
  update: async (id: string, inquiryData: InquiryData): Promise<InquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/enquiry/${id}/update`, inquiryData);
      return {
        success: true,
        data: response.data,
        message: 'Inquiry updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update inquiry'
      };
    }
  },

  // Delete inquiry
  delete: async (id: string): Promise<InquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/enquiry/${id}`);
      return {
        success: true,
        message: 'Inquiry deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting inquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete inquiry'
      };
    }
  }
};

// Host enquiry API functions
export const hostEnquiryAPI = {
  // Create new host enquiry
  create: async (hostEnquiryData: HostEnquiryData): Promise<HostEnquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/host-enquiry/create', hostEnquiryData);
      return {
        success: true,
        data: response.data,
        message: 'Host enquiry created successfully'
      };
    } catch (error: any) {
      console.error('Error creating host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create host enquiry'
      };
    }
  },

  // Get all host enquiries
  getAll: async (): Promise<HostEnquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/host-enquiry');
      return {
        success: true,
        data: response.data,
        message: 'host enquiry fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch host enquiries'
      };
    }
  },

  // Get host enquiry by ID
  getById: async (id: string): Promise<HostEnquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/host-enquiry/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'host enquiry fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch host enquiry'
      };
    }
  },

  // Update host enquiry
  update: async (id: string, hostEnquiryData: HostEnquiryData): Promise<HostEnquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/host-enquiry/${id}/update`, hostEnquiryData);
      return {
        success: true,
        data: response.data,
        message: 'Host enquiry updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update host enquiry'
      };
    }
  },

  // Delete host enquiry
  delete: async (id: string): Promise<HostEnquiryResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/host-enquiry/${id}/delete`);
      return {
        success: true,
        message: 'Host enquiry deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete host enquiry'
      };
    }
  },

  // Create a web host enquiry 
  createWeb: async (hostEnquiryData: HostEnquiryData): Promise<HostEnquiryResponse> => {
    try {
      const response = await publicApi.post('/api/v1/host-enquiry/web/create', hostEnquiryData);
      return {
        success: true,
        data: response.data,
        message: 'Host enquiry created successfully'
      };
    } catch (error: any) {
      console.error('Error creating host enquiry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create host enquiry'
      };
    }
  },
};

// Wishlist API functions
export const wishlistAPI = {
  // Add property to wishlist
  addToWishlist: async (propertyId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/wishlist/add', { propertyId });
      return {
        success: true,
        message: 'Property added to wishlist successfully'
      };
    } catch (error: any) {
      console.error('Error adding to wishlist:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add property to wishlist'
      };
    }
  },

  // Remove property from wishlist
  removeFromWishlist: async (wishlistItemId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/wishlist/${wishlistItemId}/delete`);
      return {
        success: true,
        message: 'Property removed from wishlist successfully'
      };
    } catch (error: any) {
      console.error('Error removing from wishlist:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to remove property from wishlist'
      };
    }
  },

  // Get user's wishlist
  getWishlist: async (): Promise<{ success: boolean; data?: any; message: string }> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/wishlist');
      return {
        success: true,
        data: response.data,
        message: 'Wishlist fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching wishlist:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch wishlist'
      };
    }
  },

  // Check if property is in wishlist
  isInWishlist: async (propertyId: string): Promise<{ success: boolean; isInWishlist: boolean; message: string }> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          isInWishlist: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/wishlist/check/${propertyId}`);
      return {
        success: true,
        isInWishlist: response.data.isInWishlist || false,
        message: 'Wishlist status checked successfully'
      };
    } catch (error: any) {
      console.error('Error checking wishlist status:', error);
      return {
        success: false,
        isInWishlist: false,
        message: error.response?.data?.message || 'Failed to check wishlist status'
      };
    }
  }
};

// User API functions
export const userAPI = {
  // Get all users
  getAll: async (): Promise<UserResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/auth');
      return {
        success: true,
        data: response.data,
        message: 'Users fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },

  // Get user by ID
  getById: async (id: string): Promise<UserResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/auth/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'User fetched successfully'
      };
    } catch (error: any) {
      console.error('Error fetching user:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch user'
      };
    }
  },

  // Update user
  update: async (id: string, userData: UserData): Promise<UserResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/auth/${id}/update`, userData);
      return {
        success: true,
        data: response.data,
        message: 'User updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating user:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update user'
      };
    }
  },

  // Delete user
  delete: async (id: string): Promise<UserResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/users/${id}`);
      return {
        success: true,
        message: 'User deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete user'
      };
    }
  }
};

// About Us API functions
export const aboutUsAPI = {
  // Create new about us entry
  create: async (aboutUsData: AboutUsFormData): Promise<AboutUsResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/about-us/create', aboutUsData);
      return {
        success: true,
        data: response.data,
        message: 'About Us entry created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create about us entry'
      };
    }
  },

  // Get all about us entries
  getAll: async (): Promise<AboutUsResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/about-us');
      return {
        success: true,
        data: response.data,
        message: 'About Us entries fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch about us entries'
      };
    }
  },

  // Get about us entry by ID
  getById: async (id: string): Promise<AboutUsResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/about-us/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'About Us entry fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch about us entry'
      };
    }
  },

  // Update about us entry
  update: async (id: string, aboutUsData: Partial<AboutUsFormData>): Promise<AboutUsResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/about-us/${id}/update`, aboutUsData);
      return {
        success: true,
        data: response.data,
        message: 'About Us entry updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update about us entry'
      };
    }
  },

  // Delete about us entry
  delete: async (id: string): Promise<AboutUsResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/about-us/${id}/delete`);
      return {
        success: true,
        message: 'About Us entry deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete about us entry'
      };
    }
  },

  // Get banner List
  getAboutUsList_frontend: async (): Promise<AboutUsResponse> => {
    try {
      const response = await publicApi.get('/api/v1/about-us/web/list');
      return {
        success: true,
        data: response.data,
        message: 'About Us list fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch About Us list'
      };
    }
  }
};

// Banner API
export const bannerAPI = {
  // Get all banner
  getAll: async (page: number = 1, limit: number = 10, search?: string): Promise<BannerResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search })
      });

      const response = await api.get(`/api/v1/banner?${params}`);
      return {
        success: true,
        data: response.data,
        message: 'banner fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch banner'
      };
    }
  },

  // Get banner by ID
  getById: async (id: string): Promise<BannerResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/banner/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Banner fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch banner'
      };
    }
  },

  // Create new banner
  create: async (bannerData: BannerFormData): Promise<BannerResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/banner/create', bannerData);
      return {
        success: true,
        data: response.data,
        message: 'Banner created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create banner'
      };
    }
  },

  // Update banner
  update: async (id: string, bannerData: BannerFormData): Promise<BannerResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/banner/${id}/update`, bannerData);
      return {
        success: true,
        data: response.data,
        message: 'Banner updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update banner'
      };
    }
  },

  // Delete banner
  delete: async (id: string): Promise<BannerResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/banner/${id}/delete`);
      return {
        success: true,
        data: response.data,
        message: 'Banner deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete banner'
      };
    }
  },
  // Get banner List
  getBannerList_frontend: async (): Promise<BannerResponse> => {
    try {
      const response = await publicApi.get('/api/v1/banner/web/list');
      return {
        success: true,
        data: response.data,
        message: 'Banner list fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch banner list'
      };
    }
  }
};

// Property Type API functions
export const propertyTypeAPI = {
  // Create new property type entry
  create: async (propertyTypeData: PropertyTypeFormData): Promise<PropertyTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/property-type/create', propertyTypeData);
      return {
        success: true,
        data: response.data,
        message: 'Property Type entry created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create property type entry'
      };
    }
  },

  // Get all property type entries
  getAll: async (): Promise<PropertyTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/property-type');
      return {
        success: true,
        data: response.data,
        message: 'Property Type entries fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property type entries'
      };
    }
  },

  // Get property type entry by ID
  getById: async (id: string): Promise<PropertyTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/property-type/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Property Type entry fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property type entry'
      };
    }
  },

  // Update property type entry
  update: async (id: string, propertyTypeData: Partial<PropertyTypeFormData>): Promise<PropertyTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/property-type/${id}/update`, propertyTypeData);
      return {
        success: true,
        data: response.data,
        message: 'Property Type entry updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update property type entry'
      };
    }
  },

  // Delete property type entry
  delete: async (id: string): Promise<PropertyTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/property-type/${id}/delete`);
      return {
        success: true,
        message: 'Property Type entry deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete property type entry'
      };
    }
  },

  // Get all property type entries
  getAllWeb: async (listedIn: string): Promise<PropertyTypeResponse> => {
    try {
      // const response = await api.get(`/api/v1/property-type/web/list?listedIn=${listedIn}`);
      const response = await publicApi.get(`/api/v1/mr-alfred/dictionaries?type=${listedIn}`);
      return {
        success: true,
        data: response.data,
        message: 'Property Type entries fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch property type entries'
      };
    }
  },
};

// Project Type API functions
export const projectTypeAPI = {
  // Create new project type entry
  create: async (projectTypeData: ProjectTypeFormData): Promise<ProjectTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/project-type/create', projectTypeData);
      return {
        success: true,
        data: response.data,
        message: 'Project Type entry created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create project type entry'
      };
    }
  },

  // Get all project type entries
  getAll: async (): Promise<ProjectTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/project-type');
      return {
        success: true,
        data: response.data,
        message: 'Project Type entries fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch project type entries'
      };
    }
  },

  // Get project type by ID
  getById: async (id: string): Promise<ProjectTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/project-type/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Project Type entry fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch project type entry'
      };
    }
  },

  // Update project type entry
  update: async (id: string, projectTypeData: Partial<ProjectTypeFormData>): Promise<ProjectTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.put(`/api/v1/project-type/${id}`, projectTypeData);
      return {
        success: true,
        data: response.data,
        message: 'Project Type entry updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update project type entry'
      };
    }
  },

  // Delete project type entry
  delete: async (id: string): Promise<ProjectTypeResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/project-type/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Project Type entry deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expired or invalid
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete project type entry'
      };
    }
  },
  getAllWeb: async (): Promise<ProjectTypeResponse> => {
    try {
      const response = await publicApi.get('/api/v1/project-type/web/list');
      return {
        success: true,
        data: response.data,
        message: 'Project Type entries fetched successfully'
      };
    }
    catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch project type entries'
      };
    }
  }
};

// Area API
export const areaAPI = {
  // Create new area
  create: async (areaData: AreaFormData): Promise<AreaResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/area-master/create', areaData);
      return {
        success: true,
        data: response.data,
        message: 'Area created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create area'
      };
    }
  },

  // Upsert area
  upsert: async (areaData: AreaFormData): Promise<AreaResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post('/api/v1/area-master/upsert', areaData);
      return { success: true, data: response.data, message: 'Area saved successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to save area' };
    }
  },

  // Get all areas (dashboard)
  getAll: async (): Promise<AreaResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }
      const response = await api.get('/api/v1/mr-alfred/areas');
      return { success: true, data: response.data, message: 'Areas fetched successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to fetch areas' };
    }
  },

  // Get area by id
  getById: async (id: string): Promise<AreaResponse> => {
    try {
      // const token = getAuthToken();
      // if (!token) {
      //   return { success: false, message: 'Authentication required. Please login again.' };
      // }
      const response = await api.get(`/api/v1/area-master/get/${id}`);
      return { success: true, data: response.data, message: 'Area fetched successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to fetch area' };
    }
  },

  // Update area
  update: async (id: string, areaData: Partial<AreaFormData>): Promise<AreaResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post(`/api/v1/area-master/${id}/update`, areaData);
      return { success: true, data: response.data, message: 'Area updated successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to update area' };
    }
  },

  // Delete area
  delete: async (id: string): Promise<AreaResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.delete(`/api/v1/area-master/${id}/delete`);
      return { success: true, data: response.data, message: 'Area deleted successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to delete area' };
    }
  },

  // Frontend list (no auth)
  getFrontendList: async (searchParams?: AreaSearchParams): Promise<AreaResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (searchParams?.search) {
        params.append('search', searchParams.search);
      }
      if (searchParams?.is_show_on_home) {
        params.append('is_show_on_home', searchParams.is_show_on_home.toString());
      }
      if (searchParams?.page) {
        params.append('page', searchParams.page.toString());
      }
      if (searchParams?.limit) {
        params.append('limit', searchParams.limit.toString());
      }
      if (searchParams?.per_page) {
        params.append('per_page', searchParams.per_page.toString());
      } else if (!searchParams?.search) {
        // Only add per_page=-1 if not searching (to get all areas)
        params.append('per_page', '-1');
      }

      const queryString = params.toString();
      // const url = `/api/v1/area-master/web/list${queryString ? '?' + queryString : ''}`;
      const url = `/api/v1/mr-alfred/areas${queryString ? '?' + queryString : ''}`;

      const response = await publicApi.get(url);
      return { success: true, data: response.data, message: 'Areas fetched successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to fetch areas' };
    }
  }
};

// Agent API functions
export const agentAPI = {
  // Create new agent
  create: async (agentData: AgentData): Promise<AgentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/agents/create', agentData);
      return {
        success: true,
        data: response.data,
        message: 'Agent created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create agent'
      };
    }
  },

  // Get all agents with optional query parameters
  getAll: async (queryParams?: string): Promise<AgentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const url = queryParams ? `/api/v1/agents?${queryParams}` : '/api/v1/agents';
      const response = await api.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Agents fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch agents'
      };
    }
  },

  // Get agent by ID
  getById: async (id: string): Promise<AgentResponse> => {
    try {
      // const token = getAuthToken();
      // if (!token) {
      //   return {
      //     success: false,
      //     message: 'Authentication required. Please login again.'
      //   };
      // }

      const response = await api.get(`/api/v1/agents/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Agent fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch agent'
      };
    }
  },

  // Update agent
  update: async (id: string, agentData: Partial<AgentData>): Promise<AgentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/agents/${id}/update`, agentData);
      return {
        success: true,
        data: response.data,
        message: 'Agent updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update agent'
      };
    }
  },

  // Delete agent
  delete: async (id: string): Promise<AgentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/agents/${id}/delete`);
      return {
        success: true,
        message: 'Agent deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete agent'
      };
    }
  },

  // Get agents for frontend
  getFrontendList: async (queryParams?: any): Promise<AgentResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // Handle array values (like specialties, languages)
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/agents/web/list?${queryString}` : '/api/v1/agents/web/list';

      const response = await publicApi.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Agents fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch agents'
      };
    }
  }
};

// Blog API functions
export const blogAPI = {
  // Create new blog
  create: async (blogData: BlogData): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/blogs/create', blogData);
      return {
        success: true,
        data: response.data,
        message: 'Blog created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create blog'
      };
    }
  },

  // Upsert blog (create or update)
  upsert: async (blogData: BlogData): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/blogs/upsert', blogData);
      return {
        success: true,
        data: response.data,
        message: 'Blog saved successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to save blog'
      };
    }
  },

  // Get all blogs with optional search parameters
  getAll: async (searchParams?: BlogSearchParams): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/blogs?${queryString}` : '/api/v1/blogs';

      const response = await api.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Blogs fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch blogs'
      };
    }
  },

  // Get blog by ID
  getById: async (id: string): Promise<BlogResponse> => {
    try {
      const response = await api.get(`/api/v1/blogs/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Blog fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch blog'
      };
    }
  },

  // Update blog
  update: async (id: string, blogData: Partial<BlogData>): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/blogs/${id}/update`, blogData);
      return {
        success: true,
        data: response.data,
        message: 'Blog updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update blog'
      };
    }
  },

  // Delete blog
  delete: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/blogs/${id}/delete`);
      return {
        success: true,
        message: 'Blog deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete blog'
      };
    }
  },

  // Get blogs for frontend (no authentication required)
  getFrontendList: async (searchParams?: BlogSearchParams): Promise<BlogResponse> => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(item => {
                if (item !== '') {
                  params.append(key, item.toString());
                }
              });
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/api/v1/blogs/web/list?${queryString}` : '/api/v1/blogs/web/list';

      const response = await api.get(url);
      return {
        success: true,
        data: response.data,
        message: 'Blogs fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch blogs'
      };
    }
  },

  // Add view to blog
  addView: async (id: string): Promise<BlogResponse> => {
    try {
      const response = await api.post(`/api/v1/blogs/${id}/view`);
      return {
        success: true,
        data: response.data,
        message: 'View added successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add view'
      };
    }
  },

  // Add like to blog
  addLike: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/blogs/${id}/like`);
      return {
        success: true,
        data: response.data,
        message: 'Like added successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add like'
      };
    }
  },

  // Check if user has liked blog
  hasUserLiked: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/blogs/${id}/like`);
      return {
        success: true,
        data: response.data,
        message: 'Like status checked successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to check like status'
      };
    }
  },

  // Add comment to blog (authenticated)
  addComment: async (id: string, commentData: CommentData): Promise<CommentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/blogs/${id}/comments`, commentData);
      return {
        success: true,
        data: response.data,
        message: 'Comment added successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add comment'
      };
    }
  },

  // Add comment to blog (web - no authentication required)
  addCommentWeb: async (id: string, commentData: CommentData): Promise<CommentResponse> => {
    try {
      const response = await api.post(`/api/v1/blogs/${id}/comments/web`, commentData);
      return {
        success: true,
        data: response.data,
        message: 'Comment added successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add comment'
      };
    }
  },

  // Get comments for blog
  getComments: async (id: string): Promise<CommentResponse> => {
    try {
      const response = await api.get(`/api/v1/blogs/${id}/comments`);
      return {
        success: true,
        data: response.data,
        message: 'Comments fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch comments'
      };
    }
  },

  // Get comments for blog (web - no authentication required)
  getCommentsWeb: async (id: string): Promise<CommentResponse> => {
    try {
      const response = await api.get(`/api/v1/blogs/${id}/comments/web`);
      return {
        success: true,
        data: response.data,
        message: 'Comments fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch comments'
      };
    }
  },

  // Delete comment
  deleteComment: async (id: string, commentId: string): Promise<CommentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/blogs/${id}/comments?comment_id=${commentId}`);
      return {
        success: true,
        message: 'Comment deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete comment'
      };
    }
  },

  // Approve comment
  approveComment: async (id: string, commentId: string): Promise<CommentResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/blogs/${id}/comments/approve`, { comment_id: commentId });
      return {
        success: true,
        data: response.data,
        message: 'Comment approved successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to approve comment'
      };
    }
  },


  // Save blog for later
  saveForLater: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/blogs/${id}/save`);
      return {
        success: true,
        data: response.data,
        message: 'Saved for later successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to save for later'
      };
    }
  },

  // Unsave blog from later
  unsaveForLater: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/blogs/${id}/save`);
      return {
        success: true,
        data: response.data,
        message: 'Removed from saved successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to remove from saved'
      };
    }
  },

  // Check if user has saved blog
  hasUserSaved: async (id: string): Promise<BlogResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/blogs/${id}/save`);
      return {
        success: true,
        data: response.data,
        message: 'Save status checked successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to check save status'
      };
    }
  },
};

export const NewsletterAPI = {
  // Subscribe to newsletter
  subscribe: async (email: string): Promise<NewsletterResponse> => {
    try {
      const response = await api.post('/api/v1/newsletter/subscribe', { email });
      return response.data;
    } catch (error: any) {
      return {
        meta: {
          status: false,
          message: error.response?.data?.meta?.message || error.message || 'Failed to subscribe to newsletter'
        },
        data: {
          email: email,
          isSubscribed: false,
          _id: '',
          subscribedAt: '',
          createdAt: '',
          updatedAt: '',
          id: 0,
          __v: 0
        }
      };
    }
  },

  // Get newsletter subscriber by email
  getSubscribers: async (email: string): Promise<NewsletterResponse> => {
    try {
      const response = await api.get(`/api/v1/newsletter/get-by-email/${email}`);
      return response.data;
    } catch (error: any) {
      return {
        meta: {
          status: false,
          message: error.response?.data?.meta?.message || error.message || 'Failed to fetch newsletter subscriber'
        },
        data: {
          email: email,
          isSubscribed: false,
          _id: '',
          subscribedAt: '',
          createdAt: '',
          updatedAt: '',
          id: 0,
          __v: 0
        }
      };
    }
  },

  // Unsubscribe from newsletter
  unsubscribe: async (email: string): Promise<NewsletterResponse> => {
    try {
      const response = await api.post(`/api/v1/newsletter/unsubscribe`, {
        email
      });
      return response.data;
    } catch (error: any) {
      return {
        meta: {
          status: false,
          message: error.response?.data?.meta?.message || error.message || 'Failed to unsubscribe from newsletter'
        },
        data: {
          email: email,
          isSubscribed: false,
          _id: '',
          subscribedAt: '',
          createdAt: '',
          updatedAt: '',
          id: 0,
          __v: 0
        }
      };
    }
  }
};

export const ContactUsAPI = {
  // Subscribe to newsletter
  create: async (contactUsData: ContactUsData): Promise<ContactUsResponse> => {
    try {
      const response = await api.post('/api/v1/contact-us/web/create', contactUsData);
      return response.data;
    } catch (error: any) {
      return {
        meta: {
          status: false,
          message: error.response?.data?.meta?.message || error.message || 'Failed to create contact us'
        },
        data: {
          firstName: '',
          lastName: '',
          phone: '',
          company: '',
          property_type: null,
          property_id: null,
          project_id: null,
          agent_id: null,
          budget: '',
          timeline: '',
          message: '',
          email: ''
        }
      };
    }
  },

  getAll: async (): Promise<ContactUsResponse> => {
    try {
      const response = await api.get('/api/v1/contact-us/');
      return response.data;
    } catch (error: any) {
      return {
        meta: {
          status: false,
          message: error.response?.data?.meta?.message || error.message || 'Failed to fetch contact us'
        },
        data: {
          firstName: '',
          lastName: '',
          phone: '',
          company: '',
          property_type: null,
          property_id: null,
          project_id: null,
          agent_id: null,
          budget: '',
          timeline: '',
          message: '',
          email: ''
        }
      };
    } finally {
    }
  }
};

// Awards & Recognition API functions
export const awardAPI = {
  // Get all awards
  getAll: async (page: number = 1, limit: number = 10, search?: string): Promise<AwardResponse> => {
    try {
      // Check if user is authenticated
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search })
      });

      const response = await api.get(`/api/v1/awards-recognition?${params}`);
      return {
        success: true,
        data: response.data,
        message: 'Awards fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch awards'
      };
    }
  },

  // Get award by ID
  getById: async (id: string): Promise<AwardResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get(`/api/v1/awards-recognition/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Award fetched successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch award'
      };
    }
  },

  // Create new award
  create: async (awardData: AwardFormData): Promise<AwardResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/awards-recognition/create', awardData);
      return {
        success: true,
        data: response.data,
        message: 'Award created successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create award'
      };
    }
  },

  // Update award
  update: async (id: string, awardData: AwardFormData): Promise<AwardResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/awards-recognition/${id}/update`, awardData);
      return {
        success: true,
        data: response.data,
        message: 'Award updated successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update award'
      };
    }
  },

  // Delete award
  delete: async (id: string): Promise<AwardResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/awards-recognition/${id}/delete`);
      return {
        success: true,
        data: response.data,
        message: 'Award deleted successfully'
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return {
          success: false,
          message: 'Session expired. Please login again.'
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete award'
      };
    }
  },

  // Get all awards for frontend (web/list)
  getAllWeb: async (): Promise<AwardResponse> => {
    try {
      const response = await api.get('/api/v1/awards-recognition/web/list');
      return {
        success: true,
        data: response.data,
        message: 'Awards fetched successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch awards'
      };
    }
  }
};

export const mortgageCalculatorAPI = {
  // Create mortgage calculation record
  create: async (payload: MortgageCreateRequest): Promise<MortgageCreateResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/mortgage-calculator/create', payload);
      // Normalize output shape like other APIs in this file
      if (response?.data?.meta) {
        return response.data as MortgageCreateResponse;
      }
      return {
        success: true,
        data: response.data,
        message: 'Mortgage calculation created successfully'
      };
    } catch (error: unknown) {
      let message = 'Failed to create mortgage calculation';
      let meta: { status: boolean; message: string } | undefined = undefined;
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || error.message || message;
        meta = error.response?.data?.meta;
      } else if (error instanceof Error) {
        message = error.message || message;
      }
      return {
        success: false,
        message,
        meta
      };
    }
  }
};

// Contact Host API
export const contactHostAPI = {
  create: async (contactHostData: ContactHostFormData): Promise<PropertyResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post('/api/v1/contact-host/create', contactHostData);
      return { success: true, data: response.data, message: 'Contact host created successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to create contact host' };
    }
  },

  upsert: async (contactHostData: ContactHostFormData): Promise<PropertyResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post('/api/v1/contact-host/upsert', contactHostData);
      return { success: true, data: response.data, message: 'Contact host upserted successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to upsert contact host' };
    }
  },

  update: async (id: string | number, contactHostData: ContactHostFormData): Promise<PropertyResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post(`/api/v1/contact-host/${id}/update`, contactHostData);
      return { success: true, data: response.data, message: 'Contact host updated successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to update contact host' };
    }
  },

  delete: async (id: string | number): Promise<PropertyResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.delete(`/api/v1/contact-host/${id}/delete`);
      return { success: true, data: response.data, message: 'Contact host deleted successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to delete contact host' };
    }
  },

  getById: async (id: string | number): Promise<PropertyResponse> => {
    try {
      const response = await api.get(`/api/v1/contact-host/get/${id}`);
      return { success: true, data: response.data, message: 'Contact host retrieved successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get contact host' };
    }
  },

  getAll: async (): Promise<PropertyResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.get('/api/v1/contact-host');
      return { success: true, data: response.data, message: 'Contact hosts retrieved successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get contact hosts' };
    }
  },

  getFrontendList: async (): Promise<PropertyResponse> => {
    try {
      const response = await api.get('/api/v1/contact-host/web/list');
      return { success: true, data: response.data, message: 'Contact hosts retrieved successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get contact hosts' };
    }
  },
};

// Property Services API
export const propertyServiceAPI = {
  // Create new property service
  create: async (serviceData: PropertyServiceFormData): Promise<PropertyServiceResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post('/api/v1/property-services/create', serviceData);
      return { success: true, data: response.data, message: 'Property service created successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to create property service' };
    }
  },

  // Upsert property service
  upsert: async (serviceData: PropertyServiceFormData): Promise<PropertyServiceResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post('/api/v1/property-services/upsert', serviceData);
      return { success: true, data: response.data, message: 'Property service saved successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to save property service' };
    }
  },

  // Update property service
  update: async (id: string, serviceData: Partial<PropertyServiceFormData>): Promise<PropertyServiceResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.post(`/api/v1/property-services/${id}/update`, serviceData);
      return { success: true, data: response.data, message: 'Property service updated successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to update property service' };
    }
  },

  // Delete property service
  delete: async (id: string | number): Promise<PropertyServiceResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.delete(`/api/v1/property-services/${id}/delete`);
      return { success: true, data: response.data, message: 'Property service deleted successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to delete property service' };
    }
  },

  // Get property service by ID
  getById: async (id: string | number): Promise<PropertyServiceResponse> => {
    try {
      const response = await api.get(`/api/v1/property-services/get/${id}`);
      return { success: true, data: response.data, message: 'Property service retrieved successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get property service' };
    }
  },

  // Get property service by ID
  getBySlug: async (slug: string): Promise<PropertyServiceResponse> => {
    try {
      const response = await api.get(`/api/v1/property-services/get-by-slug/${slug}`);
      return { success: true, data: response.data, message: 'Property service retrieved successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get property service' };
    }
  },

  // Get all property services (admin)
  getAll: async (): Promise<PropertyServiceResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return { success: false, message: 'Authentication required. Please login again.' };
      }
      const response = await api.get('/api/v1/property-services');
      return { success: true, data: response.data, message: 'Property services retrieved successfully' };
    } catch (error: any) {
      if (error.response?.status === 401) {
        removeAuthToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return { success: false, message: 'Session expired. Please login again.' };
      }
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get property services' };
    }
  },

  // Get property services for frontend
  getFrontendList: async (): Promise<PropertyServiceResponse> => {
    try {
      const response = await api.get('/api/v1/property-services/web/list');
      return { success: true, data: response.data, message: 'Property services retrieved successfully' };
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || error.message || 'Failed to get property services' };
    }
  },
};

// FAQ API
export const faqAPI = {
  // Create new FAQ
  create: async (faqData: FAQData): Promise<FAQResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/faq/create', faqData);
      return {
        success: true,
        data: response.data,
        message: 'FAQ created successfully'
      };
    } catch (error: any) {
      console.error('Error creating FAQ:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create FAQ'
      };
    }
  },

  // Upsert FAQ
  upsert: async (faqData: FAQData): Promise<FAQResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post('/api/v1/faq/upsert', faqData);
      return {
        success: true,
        data: response.data,
        message: 'FAQ saved successfully'
      };
    } catch (error: any) {
      console.error('Error upserting FAQ:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save FAQ'
      };
    }
  },

  // Update FAQ
  update: async (id: string, faqData: Partial<FAQData>): Promise<FAQResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.post(`/api/v1/faq/${id}/update`, faqData);
      return {
        success: true,
        data: response.data,
        message: 'FAQ updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating FAQ:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update FAQ'
      };
    }
  },

  // Delete FAQ
  delete: async (id: string | number): Promise<FAQResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.delete(`/api/v1/faq/${id}/delete`);
      return {
        success: true,
        data: response.data,
        message: 'FAQ deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting FAQ:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete FAQ'
      };
    }
  },

  // Get FAQ by ID
  getById: async (id: string | number): Promise<FAQResponse> => {
    try {
      const response = await api.get(`/api/v1/faq/get/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'FAQ retrieved successfully'
      };
    } catch (error: any) {
      console.error('Error getting FAQ:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get FAQ'
      };
    }
  },

  // Get all FAQs (admin)
  getAll: async (): Promise<FAQResponse> => {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          message: 'Authentication required. Please login again.'
        };
      }

      const response = await api.get('/api/v1/faq?per_page=-1');
      return {
        success: true,
        data: response.data,
        message: 'FAQs retrieved successfully'
      };
    } catch (error: any) {
      console.error('Error getting FAQs:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get FAQs'
      };
    }
  },

  // Get FAQs for frontend
  getFrontendList: async (): Promise<FAQResponse> => {
    try {
      const response = await api.get('/api/v1/faq/web/list');
      return {
        success: true,
        data: response.data,
        message: 'FAQs retrieved successfully'
      };
    } catch (error: any) {
      console.error('Error getting FAQs:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get FAQs'
      };
    }
  },

  // Reviews API
  reviews: {
    // Get reviews for a property
    getByPropertyId: async (propertyId: string): Promise<ReviewsResponse> => {
      try {
        const response = await api.get(`/api/v1/reviews/property/${propertyId}`);
        return {
          success: true,
          data: response.data,
          message: 'Reviews retrieved successfully'
        };
      } catch (error: any) {
        console.error('Error getting reviews:', error);
        return {
          success: false,
          message: error.response?.data?.message || 'Failed to get reviews'
        };
      }
    }
  },
};
